import getCategory from "../../../utils/getCategory";
import getWeightCategory from "../../../utils/getWeightCategory";

import {
  AGE_CATEGORY_ORDER,
  FORMULA_LABELS,
  WEIGHT_EXCLUDED_LABELS,
} from "../data/repartitionData";

export const normalizeText = (value = "") =>
  String(value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

export const parseJsonArray = (value, fallback = []) => {
  if (Array.isArray(value)) return value;

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value || "[]");
      return Array.isArray(parsed) ? parsed : fallback;
    } catch {
      return fallback;
    }
  }

  return fallback;
};

export const normalizeDistributions = (rows = []) =>
  (rows || []).map((distribution) => ({
    ...distribution,
    competiteurs: parseJsonArray(distribution.competiteurs, []),
    position_aleatoire: parseJsonArray(distribution.position_aleatoire, []),
  }));

export const parseDisciplines = (competition) => {
  const raw = competition?.disciplines_competition;

  if (Array.isArray(raw)) return raw;

  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw || "[]");
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : ["Judo"];
    } catch {
      return ["Judo"];
    }
  }

  return ["Judo"];
};

export const getBirthYear = (date) => {
  if (!date) return "";
  const year = new Date(date).getFullYear();
  return Number.isFinite(year) ? year : "";
};

export const formatWeight = (value) => {
  if (value == null || value === "") return "—";
  const number = Number(String(value).replace(",", "."));
  return Number.isFinite(number) ? `${number.toFixed(1)} kg` : "—";
};

export const isRepechageFormula = (formula = "") =>
  /^tableau_repechage_base_/i.test(String(formula || ""));

export const isBracketFormula = (formula = "") =>
  /^tableau_/i.test(String(formula || ""));

export const isPoolFormula = (formula = "") =>
  /^poule_/i.test(String(formula || ""));

export const getFormulaShortLabel = (formula = "") => {
  if (FORMULA_LABELS[formula]) return FORMULA_LABELS[formula];

  if (/^poule_/i.test(formula)) {
    const number = String(formula).split("_")[1] || "?";
    return `Poule ${number}`;
  }

  if (/^tableau_base_/i.test(formula)) {
    return `Tableau ${String(formula).replace("tableau_base_", "")}`;
  }

  if (/^tableau_repechage_base_/i.test(formula)) {
    return `Repêchage ${String(formula).replace(
      "tableau_repechage_base_",
      ""
    )}`;
  }

  return formula || "—";
};

export const buildDistributionIndex = (distributions = []) => {
  const index = new Map();

  for (const distribution of distributions) {
    const ids = Array.isArray(distribution.competiteurs)
      ? distribution.competiteurs
      : [];

    const positionsRaw = distribution.position_aleatoire;
    const positionMap = new Map();

    if (Array.isArray(positionsRaw)) {
      if (positionsRaw.length > 0 && typeof positionsRaw[0] === "object") {
        positionsRaw.forEach((positionItem, indexPosition) => {
          const competitorId =
            positionItem.id ??
            positionItem.competiteur_id ??
            ids[indexPosition];

          const position =
            positionItem.pos ?? positionItem.position ?? indexPosition + 1;

          if (competitorId != null) {
            positionMap.set(Number(competitorId) || competitorId, Number(position));
          }
        });
      } else {
        ids.forEach((competitorId, indexPosition) => {
          positionMap.set(
            Number(competitorId) || competitorId,
            Number(positionsRaw[indexPosition]) || indexPosition + 1
          );
        });
      }
    } else if (positionsRaw && typeof positionsRaw === "object") {
      Object.entries(positionsRaw).forEach(([competitorId, position]) => {
        positionMap.set(Number(competitorId) || competitorId, Number(position));
      });
    } else {
      ids.forEach((competitorId, indexPosition) => {
        positionMap.set(Number(competitorId) || competitorId, indexPosition + 1);
      });
    }

    ids.forEach((competitorId) => {
      const key = Number(competitorId) || competitorId;

      index.set(key, {
        distId: distribution.id,
        tapis: Number(distribution.numero_tapis) || null,
        pos: positionMap.get(key) ?? null,
      });
    });
  }

  return index;
};

export const buildDistributionLocalNumberMap = (distributions = []) => {
  const sorted = [...(distributions || [])].sort((a, b) => {
    const ai = Number(a.id);
    const bi = Number(b.id);

    if (!Number.isNaN(ai) && !Number.isNaN(bi)) return ai - bi;

    return String(a.id).localeCompare(String(b.id), "fr", {
      numeric: true,
      sensitivity: "base",
    });
  });

  const map = new Map();

  sorted.forEach((distribution, index) => {
    map.set(Number(distribution.id) || distribution.id, index + 1);
  });

  return map;
};

export const enrichCompetiteurs = ({
  competiteurs = [],
  settings,
  disciplinesList = [],
  distributions = [],
}) => {
  const country = settings?.cate_country || "France";
  const defaultDiscipline = disciplinesList?.[0] || "Judo";
  const distributionIndex = buildDistributionIndex(distributions);
  const localNumberMap = buildDistributionLocalNumberMap(distributions);

  return (competiteurs || []).map((competiteur) => {
    const discipline = competiteur.discipline_competiteur || defaultDiscipline;
    const ageCat = getCategory(competiteur.ddn_competiteur, country, discipline);

    const weightCat = getWeightCategory(
      competiteur.genre_competiteur,
      ageCat,
      competiteur.poids_competiteur,
      country,
      discipline
    );

    const distributionInfo = distributionIndex.get(competiteur.id);
    const rawDistId = distributionInfo?.distId ?? competiteur.distribution_id ?? null;
    const distKey = rawDistId != null ? Number(rawDistId) || rawDistId : null;

    return {
      ...competiteur,
      _discipline: discipline,
      _ageCat: ageCat,
      _weightCat: weightCat,
      _birthYear: getBirthYear(competiteur.ddn_competiteur),
      _distId: rawDistId,
      _distLocalNumber:
        distKey != null ? localNumberMap.get(distKey) ?? null : null,
      _tapis: distributionInfo?.tapis ?? null,
      _distPos: distributionInfo?.pos ?? null,
      _club: String(competiteur.club_competiteur || "").trim(),
    };
  });
};

export const matchMulti = (selected = [], value) => {
  if (!Array.isArray(selected) || selected.length === 0) return true;
  return selected.map(String).includes(String(value));
};

export const parseNumberFilter = (value) => {
  if (value === "" || value == null) return null;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
};

export const filterRows = ({ rows = [], filters }) => {
  const search = normalizeText(filters.search);
  const min = parseNumberFilter(filters.weightMin);
  const max = parseNumberFilter(filters.weightMax);

  return rows.filter((row) => {
    if (!row.is_present) return false;

    const searchable = normalizeText(
      [
        row.nom_competiteur,
        row.prenom_competiteur,
        row.club_competiteur,
        row.licence_competiteur,
      ].join(" ")
    );

    const matchSearch = !search || searchable.includes(search);

    const matchStatus =
      filters.repartitionStatus === "all" ||
      (filters.repartitionStatus === "reparti" && row._distId != null) ||
      (filters.repartitionStatus === "nonReparti" && row._distId == null);

    const matchGender =
      filters.gender === "all" || row.genre_competiteur === filters.gender;

    const matchDiscipline =
      filters.discipline === "all" || row._discipline === filters.discipline;

    const matchAge = matchMulti(filters.ageCategories, row._ageCat);
    const matchYear = matchMulti(filters.years, row._birthYear);
    const matchWeightCategory = matchMulti(
      filters.weightCategories,
      row._weightCat
    );

    const weight = Number(row.poids_competiteur ?? 0);

    const matchMin = min === null || weight >= min;
    const matchMax = max === null || weight <= max;

    return (
      matchSearch &&
      matchStatus &&
      matchGender &&
      matchDiscipline &&
      matchAge &&
      matchYear &&
      matchWeightCategory &&
      matchMin &&
      matchMax
    );
  });
};

export const sortRows = ({ rows = [], sortBy, sortDir }) => {
  const defaultSorted = [...rows].sort((a, b) => {
    const aDistributed = a._distId != null;
    const bDistributed = b._distId != null;

    if (aDistributed && bDistributed) {
      const tapisA = a._tapis ?? 9999;
      const tapisB = b._tapis ?? 9999;

      if (tapisA !== tapisB) return tapisA - tapisB;

      const distA = Number(a._distId);
      const distB = Number(b._distId);

      if (!Number.isNaN(distA) && !Number.isNaN(distB) && distA !== distB) {
        return distA - distB;
      }

      if (a._distId !== b._distId) {
        return String(a._distId).localeCompare(String(b._distId));
      }

      const posA = a._distPos ?? 9999;
      const posB = b._distPos ?? 9999;

      if (posA !== posB) return posA - posB;

      return (a.id ?? 0) - (b.id ?? 0);
    }

    if (aDistributed !== bDistributed) return aDistributed ? -1 : 1;

    return (a.poids_competiteur ?? 0) - (b.poids_competiteur ?? 0);
  });

  if (sortBy !== "dist") return defaultSorted;

  const sign = sortDir === "asc" ? 1 : -1;

  return [...defaultSorted].sort((a, b) => {
    const aNull = a._distLocalNumber == null;
    const bNull = b._distLocalNumber == null;

    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;

    if (a._distLocalNumber !== b._distLocalNumber) {
      return (a._distLocalNumber - b._distLocalNumber) * sign;
    }

    const posA = a._distPos ?? 9999;
    const posB = b._distPos ?? 9999;

    if (posA !== posB) return (posA - posB) * sign;

    return (a.id ?? 0) - (b.id ?? 0);
  });
};

export const sortAgeCategories = (values = []) => {
  const rank = (label) => {
    const index = AGE_CATEGORY_ORDER.indexOf(label);
    return index === -1 ? 999 : index;
  };

  return Array.from(new Set(values.filter(Boolean)))
    .filter((label) => label !== "Inconnu")
    .sort(
      (a, b) =>
        rank(a) - rank(b) ||
        String(a).localeCompare(String(b), "fr", { sensitivity: "base" })
    );
};

export const sortWeightLabels = (labels = []) => {
  const normalizeForSort = (label) => {
    const match = String(label || "").match(/^([+-]?)(\d+(?:\.\d+)?)\s*kg$/i);

    if (!match) {
      return {
        type: "text",
        value: String(label || "").toLowerCase(),
      };
    }

    const sign = match[1] || "-";
    const number = parseFloat(match[2]);

    return {
      type: "number",
      value: sign === "+" ? 1000 + number : number,
    };
  };

  return Array.from(new Set(labels.filter(Boolean)))
    .filter((label) => !WEIGHT_EXCLUDED_LABELS.includes(label))
    .sort((a, b) => {
      const A = normalizeForSort(a);
      const B = normalizeForSort(b);

      if (A.type !== B.type) return A.type === "number" ? -1 : 1;
      if (A.value !== B.value) return A.value > B.value ? 1 : -1;

      return String(a).localeCompare(String(b), "fr", {
        sensitivity: "base",
      });
    });
};

export const getFilterOptions = (rows = []) => ({
  disciplines: Array.from(new Set(rows.map((row) => row._discipline).filter(Boolean))).sort(
    (a, b) => String(a).localeCompare(String(b), "fr")
  ),
  years: Array.from(new Set(rows.map((row) => row._birthYear).filter(Boolean))).sort(
    (a, b) => Number(a) - Number(b)
  ),
  ageCategories: sortAgeCategories(rows.map((row) => row._ageCat)),
  weightCategories: sortWeightLabels(rows.map((row) => row._weightCat)),
});

export const shuffleArray = (rows = []) => {
  const array = [...rows];

  for (let i = array.length - 1; i > 0; i -= 1) {
    let randomIndex;

    if (window.crypto?.getRandomValues) {
      const randomValues = new Uint32Array(1);
      window.crypto.getRandomValues(randomValues);
      randomIndex = randomValues[0] % (i + 1);
    } else {
      randomIndex = Math.floor(Math.random() * (i + 1));
    }

    [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
  }

  return array;
};

export const removeCompetitorFromDistribution = (distribution, competitorId) => {
  const target = Number(competitorId);

  const oldIds = Array.isArray(distribution.competiteurs)
    ? distribution.competiteurs
    : [];

  const newIds = oldIds.filter((id) => Number(id) !== target);
  const newPositions = newIds.map((_, index) => index + 1);

  return {
    ...distribution,
    competiteurs: newIds,
    position_aleatoire: newPositions,
  };
};